<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :selected="selected"
      :class="$style.container"
      :style="style.container"
    >
      <template v-if="isLink">
        <a
          :disabled="disabled"
          :href="disabled ? null : href"
          :rel="rel"
          :selected="selected"
          :target="target"
          :style="style.link"
        >
          <img
            v-bind="imgAttrs"
            :disabled="disabled"
            :selected="selected"
            :src="src"
            :class="$style.image"
            :style="style.image"
          />
        </a>
      </template>
      <template v-else-if="isRouterLink">
        <img
          v-bind="imgAttrs"
          :disabled="disabled"
          :selected="selected"
          :src="src"
          @click="onClickRouterLink"
          :class="$style.image"
          :style="style.image"
        />
      </template>
      <template v-else>
        <img
          v-bind="imgAttrs"
          :disabled="disabled"
          :selected="selected"
          :src="src"
          @click="onClickImage"
          :class="$style.image"
          :style="style.image"
        />
      </template>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { isObject, isString } from '@js/data-utils'

  export default {
    name: 'BaseImage',
    inheritAttrs: false,
    props: {
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      href: {
        type: [Object, String],
        required: false,
        default: null,
      },
      imgAttrs: {
        type: Object,
        required: false,
        default: () => {},
      },
      openInCurrentTab: {
        type: Boolean,
        required: false,
        default: false,
      },
      selected: {
        type: Boolean,
        required: false,
        default: false,
      },
      src: {
        type: [Object, String],
        required: false,
        default: '//:0',
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
      to: {
        type: Object,
        required: false,
        default: null,
      }
    },
    data: function () {
      return {
        style: {
          container: {
            display: 'flex',
            overflow: 'hidden',
          },
          image: {
            height: '100%',
            objectFit: 'cover',
            width: '100%',
          },
          link: {
            outline: 'none',
            width: '100%',
          },
          root: {},
        },
      };
    },
    created: function () {
      const proc = new CssStyleProcessor('--v2-base-image', ['selected']);

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.image,
        proc.getExtendedStyle(this.styleConfig.image, 'image'),
      );
    },
    computed: {
      isLink: function () {
        return isString(this.href) && this.href.trim().length > 0;
      },
      isRouterLink: function () {
        return isObject(this.to) && 'name' in this.to;
      },
      rel: function () {
        return this.openInCurrentTab ? null : 'noopener noreferrer';
      },
      target: function () {
        return this.openInCurrentTab ? '_self' : '_blank';
      },
    },
    methods: {
      onClickRouterLink: function () {
        if (!this.disabled) {
          this.$router.push(this.to);
        }
      },
      onClickImage: function (e) {
        if (!this.disabled) {
          this.$emit('click', e);
        }
      },
    },
  };
</script>


<style lang="stylus" module>
  .container
    background-color var(--v2-base-image-container-background-color)
    border var(--v2-base-image-container-border)
    border-radius var(--v2-base-image-container-border-radius)
    border-width var(--v2-base-image-container-border-width)
    box-shadow var(--v2-base-image-container-box-shadow)
    &[selected]
      background-color var(--v2-base-image-container-background-color-selected)
      border var(--v2-base-image-container-border-selected)
      border-radius var(--v2-base-image-container-border-radius-selected)
      border-width var(--v2-base-image-container-border-width-selected)
      box-shadow var(--v2-base-image-container-box-shadow-selected)
    &:hover
      background-color var(--v2-base-image-container-background-color-hover)
      border var(--v2-base-image-container-border-hover)
      border-radius var(--v2-base-image-container-border-radius-hover)
      border-width var(--v2-base-image-container-border-width-hover)
      box-shadow var(--v2-base-image-container-box-shadow-hover)
    &:active
      background-color var(--v2-base-image-container-background-color-active)
      border var(--v2-base-image-container-border-active)
      border-radius var(--v2-base-image-container-border-radius-active)
      border-width var(--v2-base-image-container-border-width-active)
      box-shadow var(--v2-base-image-container-box-shadow-active)
    &[disabled]
      background-color var(--v2-base-image-container-background-color-disabled)
      border var(--v2-base-image-container-border-disabled)
      border-radius var(--v2-base-image-container-border-radius-disabled)
      border-width var(--v2-base-image-container-border-width-disabled)
      box-shadow var(--v2-base-image-container-box-shadow-disabled)

  .image
    background-color var(--v2-base-image-image-background-color)
    border var(--v2-base-image-image-border)
    border-radius var(--v2-base-image-image-border-radius)
    border-width var(--v2-base-image-image-border-width)
    box-shadow var(--v2-base-image-image-box-shadow)
    cursor var(--v2-base-image-image-cursor)
    filter var(--v2-base-image-image-filter)
    &[selected]
      background-color var(--v2-base-image-image-background-color-selected)
      border var(--v2-base-image-image-border-selected)
      border-radius var(--v2-base-image-image-border-radius-selected)
      border-width var(--v2-base-image-image-border-width-selected)
      box-shadow var(--v2-base-image-image-box-shadow-selected)
      cursor var(--v2-base-image-image-cursor)
      filter var(--v2-base-image-image-filter-selected)
    &:hover
      background-color var(--v2-base-image-image-background-color-hover)
      border var(--v2-base-image-image-border-hover)
      border-radius var(--v2-base-image-image-border-radius-hover)
      border-width var(--v2-base-image-image-border-width-hover)
      box-shadow var(--v2-base-image-image-box-shadow-hover)
      cursor var(--v2-base-image-image-cursor)
      filter var(--v2-base-image-image-filter-hover)
    &:active
      background-color var(--v2-base-image-image-background-color-active)
      border var(--v2-base-image-image-border-active)
      border-radius var(--v2-base-image-image-border-radius-active)
      border-width var(--v2-base-image-image-border-width-active)
      box-shadow var(--v2-base-image-image-box-shadow-active)
      cursor var(--v2-base-image-image-cursor)
      filter var(--v2-base-image-image-filter-active)
    &[disabled]
      background-color var(--v2-base-image-image-background-color-disabled)
      border var(--v2-base-image-image-border-disabled)
      border-radius var(--v2-base-image-image-border-radius-disabled)
      border-width var(--v2-base-image-image-border-width-disabled)
      box-shadow var(--v2-base-image-image-box-shadow-disabled)
      cursor var(--v2-base-image-image-cursor-disabled)
      filter var(--v2-base-image-image-filter-disabled)
</style>
